import { ActionIcon, Badge, Group, Modal, Stack, Text, Title } from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';
import { i18nKeys, tKeys, useTranslation } from 'locales/index';
import type { ReactNode } from 'react';
import { isEmptyish, isNonNullish, isNullish, isTruthy } from 'remeda';
import type { ContactPerson } from 'shared/hooks/use-load-contact-person';
import { languageName } from 'types/locale';

type ContactPersonModalProps = {
  contactPerson: ContactPerson | null;
  isModalOpened: boolean;
  closeModal: () => void;
};

export const ContactPersonModal = ({
  contactPerson,
  isModalOpened,
  closeModal,
}: ContactPersonModalProps) => {
  const { t } = useTranslation();

  const {
    additional_line_1: additionalLine1,
    additional_line_2: additionalLine2,
    street_name: streetName,
    street_number: streetNumber,
    street_box: streetBox,
    zip_code: zipCode,
    country: countryName,
    city,
  } = contactPerson?.address || {};

  if (isNullish(contactPerson)) return null;

  const formattedNumbers = contactPerson.phones?.map(phone =>
    phone.number.replace(/(\d{2})(?=\d)/g, '$1.'),
  );

  const getModalTitle = () => {
    const fullName = `${contactPerson.first_name} ${contactPerson.last_name}`;

    if (isNullish(contactPerson)) return '';
    if (isTruthy(contactPerson.main_contact)) {
      return (
        <Group>
          <Title order={5}>{fullName}</Title>
          <Badge radius="sm" variant="outline">
            {t(tKeys.MAIN)}
          </Badge>
        </Group>
      );
    }
    return fullName;
  };

  return (
    <Modal
      radius="lg"
      size="lg"
      opened={isModalOpened}
      onClose={closeModal}
      title={getModalTitle()}
    >
      <Stack>
        <Group align="start" grow>
          {!isEmptyish(contactPerson?.emails) ? (
            <Stack gap={0}>
              <Text c="dimmed">{t(tKeys.EMAIL)}</Text>
              {contactPerson.emails.map(email => (
                <Group key={email} gap={0}>
                  <Text key={email} ta="right" c="gray.10" fw={500}>
                    {email}
                  </Text>
                  <ActionIcon
                    variant="transparent"
                    color="gray.6"
                    onClick={() => navigator.clipboard.writeText(email)}
                  >
                    <IconCopy size={18} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          ) : (
            <Stack gap={0}>
              <Text c="dimmed">{t(tKeys.EMAIL)}</Text>
              <Text c="gray.10" fw={500}>
                -
              </Text>
            </Stack>
          )}
          <StackInfo label={t(tKeys.FORM.CLIENT.CONTACT_LANG)}>
            <Text c="gray.10" fw={500}>
              {contactPerson.locale ? languageName(contactPerson.locale) : '-'}
            </Text>
          </StackInfo>
        </Group>
        <Group grow align="start">
          {!isEmptyish(formattedNumbers) ? (
            <Stack gap={0}>
              <Text c="dimmed">{t(i18nKeys.PHONE)}</Text>
              {formattedNumbers.map(number => (
                <Group gap={0} key={number}>
                  <Text key={number} c="gray.10" fw={500}>
                    {number}
                  </Text>
                  <ActionIcon
                    variant="transparent"
                    color="gray.6"
                    onClick={() => navigator.clipboard.writeText(number)}
                  >
                    <IconCopy size={18} />
                  </ActionIcon>
                </Group>
              ))}
            </Stack>
          ) : (
            <Stack gap={0}>
              <Text c="dimmed">{t(i18nKeys.PHONE)}</Text>
              <Text c="gray.10" fw={500}>
                -
              </Text>
            </Stack>
          )}
          <StackInfo label={t(tKeys.FORM.JOB_TITLE)}>
            <Text c="gray.10" fw={500}>
              {isEmptyish(contactPerson.job_title) ? '-' : contactPerson.job_title}
            </Text>
          </StackInfo>
        </Group>
        <Group align="start" grow>
          {isNonNullish(contactPerson.address) && (
            <Stack gap={0}>
              <Text c="dimmed">{t(tKeys.ADDRESS)}</Text>
              <Text c="gray.10" fw={500}>
                {streetName ?? ''}
                {streetNumber && `, ${streetNumber}`}
                {streetBox && ` ${t(tKeys.FORM.ADDRESS.BOX)} ${streetBox}`}
              </Text>
              {additionalLine1 && (
                <Text c="gray.10" fw={500}>
                  {additionalLine1}
                </Text>
              )}
              {additionalLine2 && (
                <Text c="gray.10" fw={500}>
                  {additionalLine2}
                </Text>
              )}
              <Text c="gray.10" fw={500}>
                {zipCode ?? ''}
                {city && ` ${city}`}
                {countryName && (city || zipCode) && `, ${countryName}`}
              </Text>
              {countryName && !city && !zipCode && (
                <Text c="gray.10" fw={500}>
                  {countryName}
                </Text>
              )}
            </Stack>
          )}
          {!isEmptyish(contactPerson.notifications) && (
            <Stack gap={0}>
              <Text c="dimmed">{t(tKeys.FORM.NOTIFICATIONS)}</Text>
              {contactPerson.notifications.map(notification => (
                <Text key={notification} c="gray.10" fw={500}>
                  {t((tKeys.CONTACT_PERSON.NOTIFICATIONS as any)[notification.toUpperCase()])}
                </Text>
              ))}
            </Stack>
          )}
        </Group>
      </Stack>
    </Modal>
  );
};

type StackInfoProps = {
  label: ReactNode;
  children: ReactNode;
};

export const StackInfo = ({ label, children }: StackInfoProps) => (
  <Stack gap={0}>
    {label}
    {children}
  </Stack>
);
